/**
 * Macro Tree Types
 *
 * Persisted data model for the stateful macro system. Each campaign that opts
 * in (`HostCampaignConfig.useStatefulMacroSystem === true`) can author one
 * `MacroTreeGraph` per supported `MacroTrigger`. v1 only allows the `'launch'`
 * trigger; the schema is intentionally trigger-agnostic so v2 can extend the
 * enum without protocol churn.
 *
 * See docs/feature-plans/stateful-macro-system/EXECUTION_PLAN_v2.md for the
 * full architectural context.
 */

import { z } from 'zod';
import { refineScreenshotWaitLock, type ScreenshotWaitLockGraph } from './macroTreeScreenshotWaitRefine';
import {
  isDedicatedWait,
  validateWaitSiblingCoherence,
} from './macroTreeWaitSiblingCoherence';

// ============================================================================
// TRIGGER + FAILURE ENUMS (forward-compatible)
// ============================================================================

/**
 * Which campaign-launch event a macro tree drives.
 * v1 supports only `'launch'`. v2 extends with `'join' | 'exit' | 'inactive'`
 * and the host coordinator wires the corresponding events.
 */
export const macroTriggerSchema = z.enum(['launch']);
export type MacroTrigger = z.infer<typeof macroTriggerSchema>;

/**
 * Per-node failure response when no child variant matches.
 * v1 supports only `'restart'` (full game relaunch from root, retry-budgeted).
 * v2 may add `'fail_immediately'` (skip retries, switch immediately) and
 * `'fall_through_to_legacy'` (defer to the campaign's legacy onLaunchMacro).
 */
export const macroOnFailureSchema = z.enum(['restart']);
export type MacroOnFailure = z.infer<typeof macroOnFailureSchema>;

// ============================================================================
// NODE KIND (definitive start / step / end markers)
// ============================================================================

/**
 * Explicit role of a node in the walk graph.
 *
 *  - `'start'` — the unique node the walk begins from. The API picks this
 *     node when the host sends `currentNodeId === null` on the very first
 *     MACRO_WALK_REQUEST. Exactly one `'start'` node per tree, and its id
 *     MUST equal `rootNodeId` (the redundancy is intentional — admin tools
 *     and the walk algorithm read different fields, and the validation
 *     refinement below pins them together).
 *  - `'step'` — an intermediate node. Has at least one outgoing edge. The
 *     default for any newly authored node.
 *  - `'end'` — terminal node. The API responds `outcome: 'complete'` when
 *     the host's captures match an `'end'` child. End nodes MUST have no
 *     outgoing edges (validated below).
 *  - `'wait'` — poll gate. No screenshot, no macros. The host polls until a
 *     child screenshot matches or `waitTimeoutMs` elapses. Never start/end,
 *     never a leaf, never the root. Every child must have `expectedState`.
 *
 * Migration: legacy persisted trees written before this field existed are
 * migrated on read by the realtime-api's `macroTreeService` (root → 'start',
 * leaves → 'end', else 'step'). Admin-dashboard's `MacroTreeTab` performs
 * the same fill-in client-side so the editor never sees missing kinds.
 */
export const macroNodeKindSchema = z.enum(['start', 'step', 'end', 'wait']);
export type MacroNodeKind = z.infer<typeof macroNodeKindSchema>;

/**
 * Maximum allowed value for `MacroGroup.postMacroDelayMs` (10 minutes).
 * Recorded settles may span multiple minutes (e.g. long game loads) so the cap
 * must accommodate real-world gaps. The canvas renders a wait-time badge on the
 * incoming edge (pink > 60 s, red > 300 s) to surface unusually long delays.
 */
export const MACRO_POST_MACRO_DELAY_MAX_MS = 600_000;

/** Wait-node poll interval (host sleep between Playjector captures). */
export const MACRO_WAIT_INTERVAL_MIN_MS = 250;
export const MACRO_WAIT_INTERVAL_MAX_MS = 60_000;
export const MACRO_WAIT_INTERVAL_DEFAULT_MS = 1_000;

/**
 * Campaign launch base timeout (host-owned, 8 minutes). Any Wait-node poll
 * deadline must elapse strictly before this so the wait fails first and the
 * campaign switch timeout never preempts it.
 */
export const CAMPAIGN_SWITCH_BASE_TIMEOUT_MS = 480_000;

/** Safety margin between a capped wait deadline and the switch timeout. */
export const MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS = 5_000;

/**
 * Hard ceiling the host applies to any wait deadline:
 * `CAMPAIGN_SWITCH_BASE_TIMEOUT_MS − MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS`.
 */
export const MACRO_WAIT_TIMEOUT_CAP_MS =
  CAMPAIGN_SWITCH_BASE_TIMEOUT_MS - MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS;

/**
 * Authoring maximum for NEW wait nodes — derived from the same source as the
 * host's runtime cap so the editor can never create a wait the walk will not
 * honor. Legacy trees may still contain waits up to `MACRO_WAIT_TIMEOUT_MAX_MS`.
 */
export const MACRO_WAIT_TIMEOUT_AUTHORING_MAX_MS = MACRO_WAIT_TIMEOUT_CAP_MS;

/**
 * Wire-schema maximum (10 min). Kept for backward compatibility with persisted
 * trees written before the authoring cap existed; the host still caps any
 * incoming wait at `MACRO_WAIT_TIMEOUT_CAP_MS`.
 */
export const MACRO_WAIT_TIMEOUT_MAX_MS = 600_000;
export const MACRO_WAIT_TIMEOUT_DEFAULT_MS = 60_000;

// ============================================================================
// REGION (node-level crop; Playjector honors this at hash time)
// ============================================================================

export const macroRegionSchema = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type MacroRegion = z.infer<typeof macroRegionSchema>;

/**
 * Canonical key for pairing walk captures with node `expectedState.region`.
 * Absent / degenerate (non-positive extent) -> `""` (full-frame). Otherwise
 * `x,y,width,height` after truncating each field to an integer.
 *
 * Throws when a field is present but not a finite number. Every caller MUST
 * handle that throw: both the server twin and the client twin fail closed to
 * "stale". Neither may propagate the throw to a render path.
 */
export function regionKey(region?: MacroRegion | null): string {
  if (region == null) {
    return '';
  }
  if (typeof region !== 'object' || Array.isArray(region)) {
    throw new Error('region must be an object with x, y, width, and height');
  }
  const x = sanitizeRegionInt(region.x, 'x');
  const y = sanitizeRegionInt(region.y, 'y');
  const width = sanitizeRegionInt(region.width, 'width');
  const height = sanitizeRegionInt(region.height, 'height');
  if (width <= 0 || height <= 0) {
    return '';
  }
  return `${x},${y},${width},${height}`;
}

/**
 * `expectedState.hashedRegionKey` records the region the stored `hash` was
 * ACTUALLY computed over.
 *
 *   - `hashedRegionKey === regionKey(region)`  -> hash matches the region: fresh.
 *   - `hashedRegionKey === ''`                 -> hash is full-frame.
 *   - `hashedRegionKey === undefined`          -> UNKNOWN provenance. MUST be
 *                                                 treated as stale. A node with
 *                                                 a `region` and no key was
 *                                                 hashed full-frame by a
 *                                                 recorder that could not crop.
 *
 * A migration MUST NOT synthesise `hashedRegionKey` from `region`. Doing so
 * asserts provenance it cannot know and converts a loud lock-time failure into
 * a macro that can never match (crop hash vs full-frame hash, Hamming ~32
 * against a threshold of 15).
 *
 * This helper still treats a missing key as `''` so existing load-time
 * migrations keep compiling. Callers that have not run a provenance-preserving
 * migration MUST treat `undefined` as stale themselves rather than relying on
 * this comparison.
 */
export function regionHashStale(
  expected?: { region?: MacroRegion; hashedRegionKey?: string } | null,
): boolean {
  return regionKey(expected?.region) !== (expected?.hashedRegionKey ?? '');
}

function sanitizeRegionInt(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`region.${field} must be a finite number`);
  }
  return Math.trunc(value);
}

// ============================================================================
// SNAPSHOT (one reference frame for a node's expected screen state)
// ============================================================================

/**
 * One reference screenshot for a node. A node may carry multiple snapshots
 * captured at different game-states (e.g. animation frames of a title screen);
 * the API matches a child if ANY of its snapshots matches the host-captured
 * hash within the child's hammingDistance threshold.
 *
 * `width`/`height` are extracted from the uploaded image at admin-dash time
 * and travel per-snapshot so a single node may carry frames at multiple
 * resolutions if needed.
 */
export const macroSnapshotSchema = z.object({
  /** Stable uuid v4. Primary key for the snapshot's S3 object. */
  id: z.string().uuid(),
  /** 16-char lowercase hex perceptual hash from computeMacroImageHash. */
  hash: z.string().regex(/^[0-9a-f]{16}$/),
  /** Capture resolution (extracted from the source image at upload time). */
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** S3 URL for the 480p preview shown in the editor. Never use it for rehashing. */
  previewUrl: z.string().url(),
  /** Optional native-resolution PNG used to recompute hashes after region edits. */
  sourceUrl: z.string().url().optional(),
});
export type MacroSnapshot = z.infer<typeof macroSnapshotSchema>;

// ============================================================================
// MACRO GROUP (a node in the tree — one game-state plus its transition macros)
// ============================================================================

/**
 * A single node in the macro tree. Carries:
 *   - the macros to run when entering this node (existing key=/string=/gpad= syntax),
 *   - per-node failure behavior (`onFailure`),
 *   - and (optionally) the expected screen state used by its parent to identify
 *     us as the matched child via perceptual-hash comparison.
 *
 * The root/start node never needs `expectedState` (it is selected by kind,
 * not by hash matching). Non-root nodes may omit `expectedState` when the
 * node is the SOLE child of its parent (screenshot-optional linear advance —
 * the walk auto-advances without hash comparison). When a parent has MULTIPLE
 * children (a branch), every child MUST have `expectedState` so the walk can
 * distinguish which branch to take. This invariant is enforced by
 * `macroTreeGraphSchema`'s superRefine for locked trees; the draft schema
 * is permissive.
 */
export const macroGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  position: z.object({ x: z.number(), y: z.number() }),
  /** Macro steps in DSL grammar (parsed in Phase 3, serialized in Phase 6):
   *
   * v1 (existing):
   *   key=<keyCode>@<delayMs>^<holdMs>
   *   string=<text>@<delayMs>
   *   gpad=<BTN>@<delayMs>^<holdMs>
   *
   * v2 (pointer extensions):
   *   mouse=move^<dx>,<dy>@<delayMs>          # pointer-lock relative move (pixel deltas)
   *   mouse=down^<button>@<delayMs>           # button ∈ 0|1|2 (left|right|middle)
   *   mouse=up^<button>@<delayMs>
   *   mouse=wheel^<deltaY>@<delayMs>          # signed wheel delta
   *   touch=down@<delayMs>^<x>,<y>            # single contact, x,y ∈ [0,1] element-relative
   *   touch=move@<delayMs>^<x>,<y>
   *   touch=up@<delayMs>^<x>,<y>
   *
   * v2.1 (absolute mouse click):
   *   mouse=abs^<x>,<y>@<delayMs>             # x,y ∈ [0,1] preview-normalized; delay on abs
   *   mouse=down^<button>@0                   # button follows abs; delay stays on abs
   *   mouse=up^<button>@0
   */
  macros: z.array(z.string()),
  /** What the host should do when no child variant matches. v1: 'restart'. */
  onFailure: macroOnFailureSchema,
  /**
   * Definitive role in the walk graph. See `macroNodeKindSchema` for the
   * full contract. New nodes default to `'step'`; the unique `'start'` node
   * is also referenced by `MacroTreeGraph.rootNodeId` (the validation
   * refinement on `macroTreeGraphSchema` enforces the invariant).
   */
  kind: macroNodeKindSchema,
  /**
   * Milliseconds to wait after running this node's `macros` before requesting
   * the next screenshot from Playjector. Lets the admin pad in a short
   * settle delay for animations / fade-ins that would otherwise be captured
   * mid-transition. 0 = no delay (the default).
   *
   * Derived from recorded timestamps: the gap between the last input end of
   * node N and the timestamp of the next screen-state mark. May exceed 60 s
   * for multi-minute game loads. The canvas renders a wait-time badge on the
   * incoming edge (pink > 60 s, red > 300 s).
   *
   * Travels in `MacroWalkResponsePayload.postMacroDelayMs` so the host
   * (which executes the wait) doesn't need to know the tree shape.
   */
  postMacroDelayMs: z.number().int().min(0).max(MACRO_POST_MACRO_DELAY_MAX_MS).default(0),
  /**
   * Poll interval while waiting for a screenshot match. Required on locked
   * trees when `kind === 'wait'` (child screens) or `waitEnabled === true`
   * (this node's `expectedState`). Omit otherwise. Drafts may omit.
   */
  waitIntervalMs: z
    .number()
    .int()
    .min(MACRO_WAIT_INTERVAL_MIN_MS)
    .max(MACRO_WAIT_INTERVAL_MAX_MS)
    .optional(),
  /**
   * Wall-clock budget to match a screenshot before restart. Required with
   * `waitIntervalMs` on locked Wait nodes and on `waitEnabled` nodes.
   * Must be >= `waitIntervalMs` and <= MACRO_WAIT_TIMEOUT_MAX_MS when present.
   */
  waitTimeoutMs: z
    .number()
    .int()
    .min(MACRO_WAIT_INTERVAL_MIN_MS)
    .max(MACRO_WAIT_TIMEOUT_MAX_MS)
    .optional(),
  /**
   * When true, the walk polls this node's `expectedState` until it matches
   * (or timeout) before running this node's macros. Omit or false = legacy
   * one-shot match (existing trees). Forbidden on `kind: 'wait'` (S2).
   */
  waitEnabled: z.boolean().optional(),
  expectedState: z
    .object({
      /** Maximum hamming distance (0..64) for any snapshot to count as a match. */
      hammingDistance: z.number().int().min(0).max(64),
      /** At least one reference snapshot. The API matches the captured hash against any. */
      snapshots: z.array(macroSnapshotSchema).min(1),
      /** Node-level crop in scaled snapshot pixels; applied to every snapshot size. */
      region: macroRegionSchema.optional(),
      /** Region key used when every snapshot hash was last computed. */
      hashedRegionKey: z.string().optional(),
    })
    .optional(),
});
export type MacroGroup = z.infer<typeof macroGroupSchema>;

// ============================================================================
// EDGE (parent → child relationship)
// ============================================================================

export const macroTreeEdgeSchema = z.object({
  id: z.string().uuid(),
  source: z.string().uuid(),
  target: z.string().uuid(),
});
export type MacroTreeEdge = z.infer<typeof macroTreeEdgeSchema>;

// ============================================================================
// FORK (UI-only sidecar — does NOT affect the walk)
// ============================================================================

/**
 * A fork point in the editor canvas. Forks are PURELY a visual cleanup
 * affordance: they do NOT exist in the walk algorithm and are NOT referenced
 * by `MacroTreeGraph.edges`. The canvas expands every fork into the cartesian
 * product of (ultimate-parents × ultimate-children) direct group→group edges
 * at save time, and the expanded edges are what the API/host walk.
 *
 * The editor stores forks in `metadata.forks` solely so the same clean
 * topology can be re-rendered when the admin reopens the tree. `inputs`
 * and `outputs` may reference either group ids OR other fork ids (chained
 * fan-out/fan-in is allowed in the editor).
 *
 * Walk-side consumers (MacroWalkHandler, StatefulMacroCoordinator) MUST NOT
 * read this field — it is exclusively for the admin-dashboard editor.
 */
export const macroForkSchema = z.object({
  id: z.string().uuid(),
  position: z.object({ x: z.number(), y: z.number() }),
  inputs: z.array(z.string().uuid()),
  outputs: z.array(z.string().uuid()),
});
export type MacroFork = z.infer<typeof macroForkSchema>;

// ============================================================================
// TREE GRAPH (entire macro tree for one (campaign, trigger) pair)
// ============================================================================
//
// Two flavors live here:
//
//   • macroTreeGraphSchema       — STRICT (used by the LOCKED slot). The
//                                   walk handler and Lock action both go
//                                   through this; the superRefine block
//                                   enforces walk-readiness invariants.
//   • macroTreeGraphDraftSchema  — PERMISSIVE (used by the DRAFT slot).
//                                   Authors can persist any in-progress
//                                   topology — empty trees, missing root,
//                                   orphan edges, no 'start' node, etc.
//                                   The walk handler NEVER reads from the
//                                   draft slot, so structural-only checks
//                                   are sufficient.
//

const macroTreeGraphBaseSchema = z.object({
  /** Which trigger this tree drives. v1: always 'launch'. */
  trigger: macroTriggerSchema,
  /** Walk starts here. Must reference a node in `nodes` for the strict schema; may be empty in drafts. */
  rootNodeId: z.string().uuid(),
  nodes: z.array(macroGroupSchema).min(1),
  edges: z.array(macroTreeEdgeSchema),
  metadata: z.object({
    version: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    /** UI-only sidecar (see `macroForkSchema`). Optional for forward/backward compat. */
    forks: z.array(macroForkSchema).optional(),
  }),
});

/**
 * Permissive base for DRAFT trees. Differs from the strict base only in:
 *  - `rootNodeId` may be the empty string (no root selected yet).
 *  - `nodes` may be empty (clean editor canvas).
 *
 * Everything else still has to be type-correct (Zod still rejects
 * malformed JSON, wrong field types, etc.) so a draft round-trips cleanly
 * and renders without runtime crashes.
 */
const macroTreeGraphDraftBaseSchema = macroTreeGraphBaseSchema.extend({
  rootNodeId: z.string(),
  nodes: z.array(macroGroupSchema),
});

/**
 * Returns true iff the directed graph defined by (nodeIds, edges) is acyclic.
 * Implemented via Kahn's algorithm — O(N + E).
 */
function isAcyclic(nodeIds: ReadonlyArray<string>, edges: ReadonlyArray<MacroTreeEdge>): boolean {
  const inDegree = new Map<string, number>();
  for (const id of nodeIds) inDegree.set(id, 0);
  for (const edge of edges) {
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
  }
  const queue: string[] = [];
  for (const [id, deg] of inDegree.entries()) {
    if (deg === 0) queue.push(id);
  }
  let processed = 0;
  while (queue.length > 0) {
    const id = queue.shift()!;
    processed++;
    for (const edge of edges) {
      if (edge.source !== id) continue;
      const next = (inDegree.get(edge.target) ?? 0) - 1;
      inDegree.set(edge.target, next);
      if (next === 0) queue.push(edge.target);
    }
  }
  return processed === nodeIds.length;
}

/**
 * Returns true iff every node is reachable from `rootNodeId` via the directed
 * edges (source → target). BFS — O(N + E).
 */
function allNodesReachableFromRoot(
  rootNodeId: string,
  nodeIds: ReadonlyArray<string>,
  edges: ReadonlyArray<MacroTreeEdge>,
): boolean {
  const reachable = new Set<string>([rootNodeId]);
  const queue: string[] = [rootNodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of edges) {
      if (edge.source !== current) continue;
      if (reachable.has(edge.target)) continue;
      reachable.add(edge.target);
      queue.push(edge.target);
    }
  }
  return nodeIds.every(id => reachable.has(id));
}

export const macroTreeGraphSchema = macroTreeGraphBaseSchema
  .superRefine((graph, ctx) => {
    const nodeIds = graph.nodes.map(n => n.id);
    const seen = new Set<string>();
    for (const id of nodeIds) {
      if (seen.has(id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate node id: ${id}`, path: ['nodes'] });
        return;
      }
      seen.add(id);
    }
    const edgeIds = graph.edges.map(e => e.id);
    const seenEdges = new Set<string>();
    for (const id of edgeIds) {
      if (seenEdges.has(id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate edge id: ${id}`, path: ['edges'] });
        return;
      }
      seenEdges.add(id);
    }
    if (!seen.has(graph.rootNodeId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'rootNodeId must reference a node in nodes',
        path: ['rootNodeId'],
      });
      return;
    }
    for (const edge of graph.edges) {
      if (!seen.has(edge.source) || !seen.has(edge.target)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `edge ${edge.id} references unknown node`,
          path: ['edges'],
        });
        return;
      }
    }
    for (const node of graph.nodes) {
      if (!node.expectedState) continue;
      const snapIds = new Set<string>();
      for (const snap of node.expectedState.snapshots) {
        if (snapIds.has(snap.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate snapshot id ${snap.id} in node ${node.id}`,
            path: ['nodes'],
          });
          return;
        }
        snapIds.add(snap.id);
      }
    }
    // Forks live in metadata as a UI sidecar, but their ids are referenced by
    // other forks' `inputs`/`outputs` for chained fan-out/fan-in. Duplicates
    // would make those references ambiguous and break editor reconstruction.
    if (graph.metadata.forks) {
      const forkIds = new Set<string>();
      for (const fork of graph.metadata.forks) {
        if (forkIds.has(fork.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate fork id: ${fork.id}`,
            path: ['metadata', 'forks'],
          });
          return;
        }
        forkIds.add(fork.id);
      }
    }
    if (!isAcyclic(nodeIds, graph.edges)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'graph must be acyclic', path: ['edges'] });
      return;
    }
    if (!allNodesReachableFromRoot(graph.rootNodeId, nodeIds, graph.edges)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'every node must be reachable from rootNodeId',
        path: ['edges'],
      });
      return;
    }

    // Node-kind invariants. Authored explicitly per node; the legacy migrate-
    // on-read path in macroTreeService (admin-dashboard mirrors it) fills in
    // sensible defaults for trees written before this schema existed.
    const startNodes = graph.nodes.filter(n => n.kind === 'start');
    const endNodes = graph.nodes.filter(n => n.kind === 'end');
    if (startNodes.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `expected exactly 1 node with kind === 'start' (got ${startNodes.length})`,
        path: ['nodes'],
      });
      return;
    }
    if (startNodes[0].id !== graph.rootNodeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the 'start' node's id must equal rootNodeId",
        path: ['rootNodeId'],
      });
      return;
    }
    if (endNodes.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "expected at least 1 node with kind === 'end'",
        path: ['nodes'],
      });
      return;
    }
    // A leaf is a node with no outgoing edges. Every leaf MUST be 'end',
    // and conversely every 'end' MUST be a leaf. Together these mean the
    // walk algorithm can use `kind === 'end'` as a definitive terminal
    // signal without falling back to topology inspection.
    const outDegree = new Map<string, number>();
    for (const id of nodeIds) outDegree.set(id, 0);
    for (const edge of graph.edges) {
      outDegree.set(edge.source, (outDegree.get(edge.source) ?? 0) + 1);
    }
    for (const node of graph.nodes) {
      const isLeaf = (outDegree.get(node.id) ?? 0) === 0;
      if (isLeaf && node.kind !== 'end') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} has no outgoing edges but kind !== 'end'`,
          path: ['nodes'],
        });
        return;
      }
      if (node.kind === 'end' && !isLeaf) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'end' but has outgoing edges`,
          path: ['nodes'],
        });
        return;
      }
    }

    // Multi-child screenshot invariant: when a parent has >1 child, every
    // child must carry expectedState so the walk can distinguish branches
    // via perceptual-hash matching — except dedicated Wait siblings, which
    // carry captures on *their* children, not on themselves. A single child
    // may omit expectedState (screenshot-optional linear advance).
    const childrenByParent = new Map<string, string[]>();
    for (const edge of graph.edges) {
      const list = childrenByParent.get(edge.source) ?? [];
      list.push(edge.target);
      childrenByParent.set(edge.source, list);
    }
    for (const [parentId, childIds] of childrenByParent.entries()) {
      if (childIds.length <= 1) continue;
      const siblings = childIds
        .map(id => graph.nodes.find(n => n.id === id))
        .filter((n): n is (typeof graph.nodes)[number] => n !== undefined);
      for (const issue of validateWaitSiblingCoherence(parentId, siblings)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: ['nodes'],
        });
        return;
      }
      for (const child of siblings) {
        if (isDedicatedWait(child)) continue;
        if (!child.expectedState) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `node ${child.id} has no expectedState but is one of ${childIds.length} children of node ${parentId} — all siblings must have screenshots when branching`,
            path: ['nodes'],
          });
          return;
        }
      }
    }

    // Wait-node invariants (ERROR_STATES A1–A9). Draft schema does not run this.
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
    for (const node of graph.nodes) {
      if (node.kind !== 'wait') continue;

      if (node.id === graph.rootNodeId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but is the root — Wait cannot be start/root`,
          path: ['nodes'],
        });
        return;
      }
      if (node.expectedState) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but has expectedState — Wait must not carry a screenshot`,
          path: ['nodes'],
        });
        return;
      }
      if (node.macros.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but has macros — Wait must not carry macros`,
          path: ['nodes'],
        });
        return;
      }
      if (node.postMacroDelayMs !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but postMacroDelayMs is ${node.postMacroDelayMs} — must be 0`,
          path: ['nodes'],
        });
        return;
      }
      if (node.waitIntervalMs === undefined || node.waitTimeoutMs === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but waitIntervalMs/waitTimeoutMs are required on a locked tree`,
          path: ['nodes'],
        });
        return;
      }
      if (node.waitTimeoutMs < node.waitIntervalMs) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} waitTimeoutMs (${node.waitTimeoutMs}) must be >= waitIntervalMs (${node.waitIntervalMs})`,
          path: ['nodes'],
        });
        return;
      }

      const waitChildren = childrenByParent.get(node.id) ?? [];
      if (waitChildren.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'wait' but has no outgoing edges — Wait cannot be a leaf`,
          path: ['nodes'],
        });
        return;
      }
      for (const childId of waitChildren) {
        const child = nodeById.get(childId);
        if (child && !child.expectedState) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `node ${childId} has no expectedState but is a child of wait node ${node.id} — every Wait child must have a screenshot`,
            path: ['nodes'],
          });
          return;
        }
        if (child?.kind === 'wait') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `node ${childId} is kind: 'wait' nested under wait node ${node.id} — Wait cannot parent Wait`,
            path: ['nodes'],
          });
          return;
        }
      }
    }

    // Screenshot-node wait (ERROR_STATES S1–S5). Draft schema does not run this.
    refineScreenshotWaitLock(graph as ScreenshotWaitLockGraph, ctx);
  });
export type MacroTreeGraph = z.infer<typeof macroTreeGraphSchema>;

/**
 * Permissive schema for the DRAFT slot. NO superRefine — authors can save
 * a half-finished graph (empty, missing root, no end node, leaves with
 * kind: 'step', orphan edge refs, cycles) and reload it intact. The Lock
 * action re-validates against `macroTreeGraphSchema` before promoting a
 * draft to the locked slot.
 *
 * Walk-side consumers MUST NOT read from drafts; the realtime-api walk
 * handler is wired to the locked slot only.
 */
export const macroTreeGraphDraftSchema = macroTreeGraphDraftBaseSchema;
export type MacroTreeGraphDraft = z.infer<typeof macroTreeGraphDraftSchema>;
